// src/pages/client/timesheets/TimesheetsList.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stack,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Snackbar,
  Alert
} from '@mui/material';
import { GetApp as DownloadIcon, Comment as CommentIcon, Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import MainCard from 'components/MainCard';
import InvoiceStatusChip from 'components/invoices/InvoiceStatusChip';
import invoiceService from 'services/invoiceService';
import projectService from 'services/projectService';
import { useSnackbar } from 'hooks/useSnackbar';
import { format } from 'date-fns';

const TimesheetsList = () => {
  const { showSuccess, showError, snackbar, showWarning, handleClose } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Timesheets data - flattened from invoices to show one row per timesheet
  const [allTimesheets, setAllTimesheets] = useState([]);

  // Filters
  const [projectFilter, setProjectFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [periodFrom, setPeriodFrom] = useState(null);
  const [periodTo, setPeriodTo] = useState(null);

  // Filter options
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Comment dialog
  const [commentDialog, setCommentDialog] = useState({ open: false, invoice: null, comment: '' });

  useEffect(() => {
    fetchFilterOptions();
    fetchTimesheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFilterOptions = async () => {
    try {
      // Fetch projects available to client
      const projectsRes = await projectService.getProjects({ per_page: 'all' });
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchTimesheets = async () => {
    try {
      if (!refreshing) {
        setLoading(true);
      }

      const response = await invoiceService.getClientTimesheets({ per_page: 'all' });
      const invoicesData = response.data || [];

      // Flatten invoices to show one row per timesheet
      const timesheetsFlat = [];
      invoicesData.forEach((invoice) => {
        if (invoice.timesheets && invoice.timesheets.length > 0) {
          // If invoice has multiple timesheets (vendor + subcontractors), create a row for each
          invoice.timesheets.forEach((timesheet) => {
            timesheetsFlat.push({
              ...timesheet,
              invoice_id: invoice.id,
              invoice_number: invoice.invoice_number,
              custom_invoice_number: invoice.custom_invoice_number,
              invoice_status: invoice.status,
              project: invoice.project,
              project_id: invoice.project_id,
              vendor: invoice.vendor,
              vendor_id: invoice.vendor_id,
              billing_month: invoice.billing_month,
              created_at: invoice.created_at
            });
          });
        } else {
          // Legacy invoice without separate timesheets - show as single row
          timesheetsFlat.push({
            id: invoice.id,
            invoice_id: invoice.id,
            invoice_number: invoice.invoice_number,
            custom_invoice_number: invoice.custom_invoice_number,
            invoice_status: invoice.status,
            billing_units: invoice.billing_units,
            billing_unit_type: invoice.billing_unit_type,
            billing_period_start: invoice.billing_period_start,
            billing_period_end: invoice.billing_period_end,
            timesheet_file: invoice.timesheet_file,
            project: invoice.project,
            project_id: invoice.project_id,
            vendor: invoice.vendor,
            vendor_id: invoice.vendor_id,
            billing_month: invoice.billing_month,
            created_at: invoice.created_at,
            subcontractor: null
          });
        }
      });

      setAllTimesheets(timesheetsFlat);

      // Extract unique vendors
      const uniqueVendors = invoicesData
        .map((inv) => inv.vendor)
        .filter((vendor, index, self) => vendor && self.findIndex((v) => v?.id === vendor.id) === index);
      setVendors(uniqueVendors);
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
      showError('Failed to load timesheets');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Local filtering
  const filteredTimesheets = allTimesheets.filter((timesheet) => {
    // Project filter
    if (projectFilter !== 'all' && timesheet.project_id !== parseInt(projectFilter)) {
      return false;
    }

    // Vendor filter
    if (vendorFilter !== 'all' && timesheet.vendor_id !== parseInt(vendorFilter)) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesInvoiceNumber = timesheet.invoice_number?.toLowerCase().includes(query);
      const matchesVendor = timesheet.vendor?.company_name?.toLowerCase().includes(query);
      const matchesSubcontractor =
        timesheet.subcontractor?.first_name?.toLowerCase().includes(query) ||
        timesheet.subcontractor?.last_name?.toLowerCase().includes(query);
      if (!matchesInvoiceNumber && !matchesVendor && !matchesSubcontractor) {
        return false;
      }
    }

    // Period from filter
    if (periodFrom && timesheet.billing_period_start) {
      const timesheetStart = new Date(timesheet.billing_period_start);
      if (timesheetStart < periodFrom) {
        return false;
      }
    }

    // Period to filter
    if (periodTo && timesheet.billing_period_end) {
      const timesheetEnd = new Date(timesheet.billing_period_end);
      if (timesheetEnd > periodTo) {
        return false;
      }
    }

    return true;
  });

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTimesheets();
  };

  const handleDownloadTimesheet = async (timesheet) => {
    if (!timesheet.timesheet_file) {
      showWarning('No timesheet file available for download');
      return;
    }

    try {
      let blob;

      // Check if this is an individual timesheet (has subcontractor or different ID) or legacy invoice timesheet
      const isIndividualTimesheet =
        timesheet.subcontractor || (timesheet.id && timesheet.invoice_id && timesheet.id !== timesheet.invoice_id);

      if (isIndividualTimesheet) {
        // New multi-timesheet structure - use individual timesheet download
        blob = await invoiceService.downloadIndividualTimesheetFile(timesheet.invoice_id, timesheet.id);
      } else {
        // Legacy structure - use invoice timesheet download
        blob = await invoiceService.downloadTimesheetFile(timesheet.invoice_id);
      }

      const extension = timesheet.timesheet_file.split('.').pop() || 'pdf';
      const subcontractorName = timesheet.subcontractor
        ? `_${timesheet.subcontractor.first_name}_${timesheet.subcontractor.last_name}`.replace(/\s+/g, '_')
        : '';
      const filename = `timesheet_${timesheet.invoice_number}${subcontractorName}.${extension}`;
      invoiceService.downloadFile(blob, filename);
      showSuccess('Timesheet downloaded successfully');
    } catch (error) {
      console.error('Failed to download timesheet:', error);
      showError(error.response?.data?.message || 'Failed to download timesheet');
    }
  };

  const handleOpenCommentDialog = (timesheet) => {
    setCommentDialog({ open: true, invoice: timesheet, comment: '' });
  };

  const handleCloseCommentDialog = () => {
    setCommentDialog({ open: false, invoice: null, comment: '' });
  };

  const handleSubmitComment = async () => {
    if (!commentDialog.comment.trim()) {
      showWarning('Please enter a comment');
      return;
    }

    try {
      await invoiceService.addTimesheetComment(commentDialog.invoice.invoice_id, commentDialog.comment);
      handleCloseCommentDialog();
      fetchTimesheets();
      showSuccess('Comment added successfully');
    } catch (error) {
      console.error('Failed to add comment:', error);
      showError(error.response?.data?.message || 'Failed to add comment');
    }
  };

  const formatBillingMonth = (billingMonth) => {
    if (!billingMonth) return '-';
    try {
      // billingMonth is in format YYYY-MM
      const [year, month] = billingMonth.split('-');
      const date = new Date(year, month - 1, 1);
      return format(date, 'MMM yyyy');
    } catch {
      return billingMonth;
    }
  };

  const getWorkerName = (timesheet) => {
    if (timesheet.subcontractor) {
      return `${timesheet.subcontractor.first_name || ''} ${timesheet.subcontractor.last_name || ''}`.trim();
    }
    return `${timesheet.vendor?.user?.first_name || ''} ${timesheet.vendor?.user?.last_name || ''}`.trim();
  };

  if (loading && !refreshing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <MainCard
          title="Timesheets Needing Your Review"
          secondary={
            <Button startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={refreshing} variant="outlined">
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          }
        >
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Invoice number, vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth select label="Project" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                <MenuItem value="all">All Projects</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField fullWidth select label="Vendor" value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}>
                <MenuItem value="all">All Vendors</MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.company_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
              <DatePicker
                label="From Date"
                value={periodFrom}
                onChange={(newValue) => setPeriodFrom(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true
                  },
                  field: { clearable: true }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 1.5 }}>
              <DatePicker
                label="To Date"
                value={periodTo}
                onChange={(newValue) => setPeriodTo(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true
                  },
                  field: { clearable: true }
                }}
              />
            </Grid>
          </Grid>

          {/* Summary */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary'
              }}
            >
              {filteredTimesheets.length} timesheet{filteredTimesheets.length !== 1 ? 's' : ''} found
              {allTimesheets.length !== filteredTimesheets.length && <> (filtered from {allTimesheets.length} total)</>}
            </Typography>
          </Box>

          {/* Timesheets Table */}
          {filteredTimesheets.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography
                sx={{
                  color: 'text.secondary'
                }}
              >
                No timesheets found
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Billing Month</TableCell>
                    <TableCell>Worker</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="center">Hours/Days</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTimesheets.map((timesheet, index) => (
                    <TableRow key={`${timesheet.invoice_id}-${timesheet.id || index}`} hover>
                      <TableCell>
                        <Typography variant="body2">{formatBillingMonth(timesheet.billing_month)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{getWorkerName(timesheet)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{timesheet.project?.name || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="subtitle2">
                          {timesheet.billing_units || 0} {timesheet.billing_unit_type || 'hours'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <InvoiceStatusChip status={timesheet.invoice_status === 'paid' ? 'approved' : timesheet.invoice_status} />
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{
                            justifyContent: 'center'
                          }}
                        >
                          {timesheet.timesheet_file && (
                            <Tooltip title="Download Timesheet">
                              <IconButton size="small" onClick={() => handleDownloadTimesheet(timesheet)}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Add Comment">
                            <IconButton size="small" color="primary" onClick={() => handleOpenCommentDialog(timesheet)}>
                              <CommentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </MainCard>

        {/* Comment Dialog */}
        <Dialog open={commentDialog.open} onClose={handleCloseCommentDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add Comment to Timesheet</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comment"
              value={commentDialog.comment}
              onChange={(e) => setCommentDialog({ ...commentDialog, comment: e.target.value })}
              placeholder="Enter your comment..."
              autoFocus
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCommentDialog}>Cancel</Button>
            <Button onClick={handleSubmitComment} variant="contained" color="primary" disabled={!commentDialog.comment.trim()}>
              Add Comment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default TimesheetsList;
